import AnalyticsEvent = ToroAnalyticsEnums.AnalyticsEvent;

import { Component, OnInit } from '@angular/core';
import { finalize, take } from 'rxjs/operators';
import { I3AssetWorkOrders, I3OpenWorkOrders } from '../../../../../api/intelli360/models/i3-open-work-orders.model';
import { AnalyticsService } from '../../../../../common/services/analytics.service';
import { AuthManagerService } from '../../../../../api/auth/auth-manager.service';
import { BroadcastService } from '../../../../../common/services/broadcast.service';
import { DashMessageService } from '../../../../../common/services/dash-message.service';
import { DashUserManagerService } from '../../../../../api/dash-user/dash-user-manager.service';
import { DeviceManagerService } from '../../../../../common/services/device-manager.service';
import { environment } from '../../../../../../environments/environment';
import { HttpStatusCode } from '@angular/common/http';
import { Intelli360ManagerService } from '../../../../../api/intelli360/intelli360-manager.service';
import { Intelli360WidgetBase } from '../_intelli360-widget-base';
import { ToroAnalyticsEnums } from '../../../../../common/enumerations/analytics.enums';
import { TranslateService } from '@ngx-translate/core';

@Component({
    selector: 'toro-widget-intelli360-work-orders',
    templateUrl: './widget-intelli360-work-orders.component.html',
    styleUrls: ['./widget-intelli360-work-orders.component.less']
})
export class WidgetIntelli360WorkOrdersComponent extends Intelli360WidgetBase implements OnInit {

    iconColor = 'black';
    title = 'WIDGET.INTELLI360_WORK_ORDERS';

    protected showDesktopModal = false;
    protected isDemoMode = false;
    protected workOrders: I3OpenWorkOrders;
    protected selectedAsset: I3AssetWorkOrders;

    protected groupedWorkOrders: I3AssetWorkOrders[];

    // =========================================================================================================================================================
    // Ctor and Lifecycle Hooks
    // =========================================================================================================================================================

    constructor(protected analyticsService: AnalyticsService,
                private authManager: AuthManagerService,
                protected broadcastService: BroadcastService,
                private dashMessageService: DashMessageService,
                protected dashUserManager: DashUserManagerService,
                protected deviceManager: DeviceManagerService,
                private intelli360Manager: Intelli360ManagerService,
                protected translateService: TranslateService,
    ) {
        super(analyticsService, broadcastService, dashUserManager, deviceManager, intelli360Manager, translateService);
    }

    ngOnInit(): void {
        super.ngOnInit();

        this.isDemoMode = environment.isDemoMode;
        if (this.isDemoMode) { this.title = ''; }

        // NOTE: Initial data is fetched in the base class call to super.widgetResized. This is to ensure we don't attempt to load
        // any widget content until the widget has been properly sized (i.e., displayCols/displayRows has been properly set.
    }

    // =========================================================================================================================================================
    // Base Class Overrides
    // =========================================================================================================================================================

    get analyticsWidgetName(): string {
        return AnalyticsEvent.Intelli360MaintenanceWidgetName;
    }

    protected getWidgetData() {
        if (this.isDemoMode) {
            // In demo mode, we do not fetch real data. We show an image.
            this.isBusy = false;
            return;
        }

        if (!this.isIntelli360UserLoggedIn) { return; }
        if (this.authManager.isDoingIntelli360Login) { return; }
        if (this.isUnableToFetchData) { this.isBusy = true; }

        this.intelli360Manager.getOpenWorkOrders()
            .pipe(
                take(1),
                finalize(() => {
                    if (this.selectedAsset == null && this.groupedWorkOrders.length > 0) { this.selectedAsset = this.groupedWorkOrders[0]; }
                    this.isWidgetInitialized = true;
                    this.isBusy = false;
                })
            )
            .subscribe({
                next: (response: I3OpenWorkOrders) => {
                    this.workOrders = response;

                    // TODO: Do we need to filter the list of Work Orders?
                    // this.workOrders.openWorkOrderModelList = this.workOrders.openWorkOrderModelList.filter(a => a.workOrderStateId > 1);

                    this.groupedWorkOrders = Object.values(
                        this.workOrders.openWorkOrderModelList.reduce((acc, workOrder) => {
                            if (!acc[workOrder.assetId]) {
                                acc[workOrder.assetId] = new I3AssetWorkOrders({
                                    assetId: workOrder.assetId,
                                    workOrders: []
                                });
                            }
                            acc[workOrder.assetId].workOrders.push(workOrder);
                            return acc;
                        }, {} as { [assetId: number]: I3AssetWorkOrders })
                    );

                    if (this.groupedWorkOrders.length == 0) { this.selectedAsset = null; }

                    this.lastUpdateTimestamp = new Date();
                    this.clearIsUnableToFetchData();
                }, error: err => {
                    this.isIntelli360UserLoggedIn = err.status !== HttpStatusCode.FailedDependency;
                    if (!this.isIntelli360UserLoggedIn) { this.intelli360UserName = ''; }

                    this.setIsUnableToFetchData(!this.isIntelli360UserLoggedIn ? 'ERR_MSG.I3_USER_NOT_LOGGED_IN' : 'ERR_MSG.SERVER_ERROR');
                    if (this.isWidgetInitialized) { this.dashMessageService.showWidgetDataFetchErrorMessage(this.title); }
                }
            });
    }

    // =========================================================================================================================================================
    // Event Handlers
    // =========================================================================================================================================================

    onLinkClick() {
        this.broadcastService.toggleSystemOverlay.next({ show: true, text: 'STRINGS.NAVIGATING_TO_INTELLI360' });
        setTimeout(() => window.open(environment.intelli360SiteUrl, '_blank'), 1000);
        setTimeout(() => this.broadcastService.toggleSystemOverlay.next({ show: false }), 2000);
    }
}
